import { useState } from 'react';
import {
  Drawer,
  Button,
  Space,
  Typography,
  message,
  Upload,
  Tag,
  Alert,
  Result,
  Divider,
} from 'antd';
import {
  CloseOutlined,
  InboxOutlined,
  UploadOutlined,
  FileMarkdownOutlined,
  CheckCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import type { UploadProps } from 'antd';
import { importSkillFromMarkdown, type ImportSkillResult } from '@/api/skills';
import { colors, typography } from '@/theme/tokens';

const { Text, Title, Paragraph } = Typography;
const { Dragger } = Upload;

// ─────────────────────────────────────────────────────────────────────────────
// SkillImportDrawer — drag/drop or pick a .md file → preview frontmatter →
// commit to the database. The server reuses the same parser the on-disk
// provisioning tests use (IAgentSkillFileLoader.Parse), so a file that
// imports successfully here will also pass the CI clinical-gates check.
//
// Accepts the canonical skill format documented in
// docs/claude/agent-skill-authoring.md:
//
//   ---
//   name: <kebab-case-id>
//   category: Clinical Safety
//   sortOrder: 12
//   alwaysLoad: false
//   description: >-
//     Router-facing summary…
//   ---
//
//   ## Heading
//   Body…
//
// UX intent (per "non-tech-savvy users" guidance):
//   - One screen does it all: pick file → see what we read → commit.
//   - File parser errors come from the SERVER and are author-actionable
//     ("frontmatter 'name' is X but filename is Y" etc.). We render them
//     verbatim — no "Something went wrong".
//   - Idempotent imports show a friendly "Updated existing skill" or
//     "No change — already up to date" instead of a generic success toast.
// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  onClose: () => void;
  /** Called after a successful import so the parent list refreshes. */
  onImported?: (result: ImportSkillResult) => void;
}

interface PickedFile {
  name: string;
  content: string;
  size: number;
}

interface ParsedPreview {
  name: string | null;
  category: string | null;
  sortOrder: number | null;
  alwaysLoad: boolean | null;
  description: string | null;
  bodyLength: number;
  /** Lines that look like section headings — surfaced in the "what we'll save" panel. */
  headings: string[];
}

export function SkillImportDrawer({ open, onClose, onImported }: Props) {
  const [picked, setPicked] = useState<PickedFile | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [resultView, setResultView] = useState<ImportSkillResult | null>(null);

  function reset(): void {
    setPicked(null);
    setSubmitting(false);
    setServerError(null);
    setResultView(null);
  }

  function handleClose(): void {
    if (!submitting) {
      reset();
      onClose();
    }
  }

  // ── Read the picked file's text into memory ─────────────────────────
  // Limit to 1 MB — the longest skill bodies in the repo are ~30 KB. A
  // bigger file is almost certainly a wrong-file mistake; bail before we
  // spend a network round-trip on it.
  const draggerProps: UploadProps = {
    multiple: false,
    accept: '.md,text/markdown',
    showUploadList: false,
    beforeUpload: async (file) => {
      const max = 1024 * 1024;
      if (file.size > max) {
        void message.error(`File is too large (${(file.size / 1024).toFixed(0)} KB). Maximum is 1 MB.`);
        return Upload.LIST_IGNORE;
      }
      try {
        const content = await readFileAsText(file);
        setPicked({ name: file.name, content, size: file.size });
        setServerError(null);
        setResultView(null);
      } catch (err) {
        void message.error(err instanceof Error ? err.message : 'Could not read the file.');
      }
      // Always returning false prevents antd Upload from POSTing the file
      // itself — we manage the API call manually.
      return false;
    },
  };

  // ── Client-side preview parse ──────────────────────────────────────
  // Best-effort only: we extract the YAML frontmatter and key/value pairs
  // so the user can see "what will be saved" before committing. The server
  // is the source of truth — a successful preview here doesn't mean the
  // server will accept it (clinical gates run only on the server). When
  // the preview parse fails, we still show the upload field; the server's
  // own error message wins.
  const preview: ParsedPreview | null = picked ? safePreview(picked.content) : null;

  async function handleImport(): Promise<void> {
    if (!picked) return;
    setSubmitting(true);
    setServerError(null);
    try {
      const result = await importSkillFromMarkdown(picked.content, picked.name);
      setResultView(result);
      onImported?.(result);
    } catch (err) {
      // Server-side parser errors come through here verbatim — they read
      // like "Skill file (uploaded: foo.md): frontmatter 'name' is X but
      // filename is Y. These must match." → exactly the right level of
      // detail for the author.
      setServerError(err instanceof Error ? err.message : 'Failed to import the skill.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      placement="right"
      width="48%"
      destroyOnClose
      closable={false}
      title={null}
      styles={{
        body: { padding: 0, display: 'flex', flexDirection: 'column' },
        header: { display: 'none' },
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '20px 24px 16px',
          borderBottom: `1px solid ${colors.slate[200]}`,
          background: `linear-gradient(135deg, ${colors.violet[100]}, ${colors.slate[50]})`,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
            <UploadOutlined style={{ fontSize: 18, color: colors.violet[600] }} />
            <Title level={5} style={{ margin: 0 }}>
              Import a skill from a file
            </Title>
          </div>
          <Button icon={<CloseOutlined />} onClick={handleClose} aria-label="Close" />
        </div>
        <Paragraph type="secondary" style={{ margin: '6px 0 0', fontSize: 13, lineHeight: 1.55 }}>
          Pick a <Text code>.md</Text> file written in our skill format (a YAML header
          followed by the body). We&apos;ll show you what we&apos;re going to save before
          anything is committed. If a skill with the same name already exists, it will be
          updated in place.
        </Paragraph>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflow: 'auto', padding: '20px 24px' }}>
        {!resultView && (
          <>
            <Dragger {...draggerProps} disabled={submitting}>
              <p className="ant-upload-drag-icon">
                <InboxOutlined style={{ color: colors.violet[600] }} />
              </p>
              <p className="ant-upload-text" style={{ margin: 0, fontWeight: 600 }}>
                Drop a .md skill file here, or click to browse
              </p>
              <p className="ant-upload-hint" style={{ marginTop: 6 }}>
                Up to 1 MB. The filename should match the <Text code>name</Text> in the
                frontmatter (e.g. <Text code>abvd-fever-triage-patient.md</Text>).
              </p>
            </Dragger>

            {picked && (
              <div
                style={{
                  marginTop: 14,
                  padding: '12px 14px',
                  borderRadius: 12,
                  background: '#fff',
                  border: `1px solid ${colors.slate[200]}`,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    marginBottom: 10,
                  }}
                >
                  <FileMarkdownOutlined style={{ fontSize: 18, color: colors.violet[600] }} />
                  <Text strong style={{ fontFamily: typography.fontMono }}>
                    {picked.name}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {(picked.size / 1024).toFixed(1)} KB
                  </Text>
                </div>

                {preview ? (
                  <PreviewBlock preview={preview} />
                ) : (
                  <Alert
                    type="warning"
                    showIcon
                    message="We couldn't read the frontmatter for a preview"
                    description="That doesn't necessarily mean the file is bad — the server will give a more specific error if anything is off. You can still try importing."
                  />
                )}
              </div>
            )}

            {serverError && (
              <Alert
                style={{ marginTop: 14 }}
                type="error"
                showIcon
                message="The server didn't accept this file"
                description={
                  <Text style={{ whiteSpace: 'pre-wrap', fontFamily: typography.fontMono, fontSize: 12.5 }}>
                    {serverError}
                  </Text>
                }
              />
            )}
          </>
        )}

        {resultView && (
          <Result
            status="success"
            icon={<CheckCircleOutlined style={{ color: colors.green[600] }} />}
            title={
              resultView.status === 'Inserted'
                ? `Created "${resultView.name}"`
                : resultView.status === 'Updated'
                ? `Updated "${resultView.name}"`
                : `No change — "${resultView.name}" was already up to date`
            }
            subTitle={
              resultView.status === 'Unchanged'
                ? 'Skipping the save kept the version number unchanged for the audit log.'
                : `Saved with category "${resultView.category ?? '—'}" and sort order ${resultView.sortOrder}.`
            }
            extra={
              <Space>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => reset()}
                >
                  Import another file
                </Button>
                <Button onClick={handleClose}>Done</Button>
              </Space>
            }
          />
        )}
      </div>

      {/* Footer */}
      {!resultView && (
        <div
          style={{
            padding: '12px 24px',
            borderTop: `1px solid ${colors.slate[200]}`,
            background: '#fff',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
            <Text type="secondary" style={{ fontSize: 12.5 }}>
              Need help with the format?{' '}
              <a
                href="https://github.com/anthropics/.."
                onClick={(e) => e.preventDefault()}
                style={{ color: colors.violet[600] }}
              >
                See an example
              </a>
            </Text>
            <Space>
              <Button onClick={handleClose} disabled={submitting}>
                Cancel
              </Button>
              <Button
                type="primary"
                icon={<UploadOutlined />}
                loading={submitting}
                disabled={!picked || submitting}
                onClick={handleImport}
              >
                Import skill
              </Button>
            </Space>
          </div>
        </div>
      )}
    </Drawer>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Preview block — read-only summary of what was parsed from the file.
// ─────────────────────────────────────────────────────────────────────────────

function PreviewBlock({ preview }: { preview: ParsedPreview }) {
  return (
    <div>
      <Text strong style={{ display: 'block', marginBottom: 8, fontSize: 13 }}>
        We&apos;ll save this:
      </Text>
      <Space direction="vertical" size={6} style={{ width: '100%' }}>
        <KvRow label="Name" value={preview.name ?? '—'} mono />
        <KvRow label="Category" value={preview.category ?? '—'} />
        <KvRow
          label="Priority"
          value={preview.sortOrder !== null ? `Sort order ${preview.sortOrder}` : '—'}
        />
        <KvRow
          label="Always load"
          value={preview.alwaysLoad === true ? 'Yes' : preview.alwaysLoad === false ? 'No' : '—'}
        />
      </Space>
      <Divider style={{ margin: '12px 0' }} />
      {preview.description && (
        <>
          <Text strong style={{ display: 'block', marginBottom: 4, fontSize: 12.5 }}>
            Short description (the router reads this every turn):
          </Text>
          <Paragraph
            style={{
              fontSize: 12.5,
              color: colors.slate[600],
              background: colors.slate[50],
              padding: '8px 10px',
              borderRadius: 8,
              border: `1px solid ${colors.slate[200]}`,
              margin: 0,
            }}
          >
            {preview.description}
          </Paragraph>
        </>
      )}
      <div style={{ marginTop: 10, display: 'flex', gap: 16, alignItems: 'center' }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Body: <Text strong>{preview.bodyLength.toLocaleString()}</Text> characters
        </Text>
        {preview.headings.length > 0 && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Sections detected: {preview.headings.slice(0, 5).map((h, i) => (
              <Tag key={i} style={{ borderRadius: 999, marginRight: 4 }}>
                {h}
              </Tag>
            ))}
            {preview.headings.length > 5 && <Tag>+{preview.headings.length - 5}</Tag>}
          </Text>
        )}
      </div>
    </div>
  );
}

function KvRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
      <Text type="secondary" style={{ fontSize: 12, minWidth: 100 }}>
        {label}
      </Text>
      <Text
        style={{
          fontSize: 13,
          fontFamily: mono ? typography.fontMono : undefined,
          color: colors.slate[800],
        }}
      >
        {value}
      </Text>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tiny client-side preview parser. Best-effort — only used to show the user
// what the file looks like; the SERVER does the authoritative parse and
// returns the actual error if anything is wrong.
// ─────────────────────────────────────────────────────────────────────────────

function safePreview(content: string): ParsedPreview | null {
  try {
    const text = content.replace(/\r\n/g, '\n');
    if (!text.startsWith('---\n')) return null;
    const close = text.indexOf('\n---', 4);
    if (close < 0) return null;
    const fm = text.substring(4, close);
    const body = text.substring(close + 4).replace(/^\n+/, '');

    const out: ParsedPreview = {
      name: null,
      category: null,
      sortOrder: null,
      alwaysLoad: null,
      description: null,
      bodyLength: body.length,
      headings: [],
    };

    const lines = fm.split('\n');
    let i = 0;
    while (i < lines.length) {
      const line = lines[i];
      const colon = line.indexOf(':');
      if (colon < 0) {
        i++;
        continue;
      }
      const key = line.substring(0, colon).trim();
      let value = line.substring(colon + 1).trim();
      if (value === '|' || value === '|-' || value === '>-' || value === '>') {
        const buf: string[] = [];
        i++;
        while (i < lines.length && (lines[i].startsWith(' ') || lines[i].length === 0)) {
          buf.push(lines[i].trim());
          i++;
        }
        value = buf.join(value.startsWith('>') ? ' ' : '\n').trim();
      } else {
        i++;
      }
      if (
        (value.startsWith('"') && value.endsWith('"')) ||
        (value.startsWith("'") && value.endsWith("'"))
      ) {
        value = value.slice(1, -1);
      }
      switch (key) {
        case 'name':
          out.name = value;
          break;
        case 'category':
          out.category = value;
          break;
        case 'sortOrder': {
          const n = parseInt(value, 10);
          out.sortOrder = Number.isFinite(n) ? n : null;
          break;
        }
        case 'alwaysLoad':
          out.alwaysLoad = /^(true|yes|1)$/i.test(value);
          break;
        case 'description':
          out.description = value;
          break;
      }
    }

    out.headings = body
      .split('\n')
      .filter((l) => /^#{2,4} /.test(l.trimStart()))
      .map((l) => l.replace(/^#+\s*/, '').trim())
      .slice(0, 12);

    return out;
  } catch {
    return null;
  }
}

function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(reader.error ?? new Error('File read failed'));
    reader.readAsText(file);
  });
}
